import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Table, BaseRunners, mapCtwbToPitches, styles, pitchTypeDisplayName } from 'yankees-react-utils';
import { store } from '../../../store';
import AtBatTableRow from './AtBatTableRow';

const StyledAtBatHeader = styled.th.attrs({ className: 'table-subtitle' })`
    padding-top: 5px !important;
    padding-bottom: 5px !important;
    color: white;
    font-size: 1.1em;
    font-weight: lighter !important;
    b {
        font-weight: bold !important;
    }
    > span {
        margin-right: 10px;
    }
`;

const PitchExecutionTable = ({ playerid, pitches, flaggedPitchFilter, heatMaps, ctwb, gameid, pitchReviews, isInBats }) => {
    const { t: translate } = useTranslation();
    const { state: { user: loggedInUser } } = useContext(store);
    const canFlagPitch = loggedInUser && !loggedInUser.Roles?.find((r) => r.RoleName === 'player');

    const rowGroups = pitches
        .map((p) => ({
            ...p,
            catcherWristbandImg: heatMaps.filter((i) => i.BatterID === p.batterid),
            review: pitchReviews.find((pr) => pr.GameID === p.gameid && pr.EventSeq === p.eventseq && pr.PitchSeq === p.pitchseq),
        }))
        .filter((p) => (flaggedPitchFilter !== true || p.review))
        .reduce((acc, p) => {
            const { inning, batterid, islhb, outsbefore, bcbefore } = p;
            const key = { inning, batterid, islhb, outsbefore, bcbefore };
            if (!acc.length || Object.keys(key).some((k) => key[k] !== acc[acc.length - 1][k])) {
                acc.push({ ...p, pitches: [] });
            }
            acc[acc.length - 1].pitches.push(p);
            return acc;
        }, []);

    if (!rowGroups.length) {
        return <b className="m-2 d-flex justify-content-center text-uppercase">{translate('sections.post-game.pitch-execution.no-pitches-found')}</b>;
    }

    return rowGroups.map((g) => {
        const headerText = [
            <>{translate('baseball.inning')}: <b>{g.inning}</b></>,
            <>{translate('baseball.batter')}: <b>{g.batterusesname} {g.batterlastname}</b></>,
            <>{translate('baseball.bats')}: <b>{g.islhb ? 'L' : 'R'}</b></>,
            <>{translate('baseball.outs_other')}: <b>{g.outsbefore}</b></>,
            <>{translate('baseball.runners_other')}: <BaseRunners baseState={g.bcbefore} height={20} width={30} /></>,
        ];
        const mappedPitches = mapCtwbToPitches(ctwb, playerid, g.pitches, translate);
        const [{ pitcherRepertoire }] = mappedPitches;
        return (
            <Table key={`t_${g.eventseq}-${g.pitchseq}`}>
                <React.Fragment key={`${g.eventseq}-${g.pitchseq}`}>
                    <thead>
                        <tr>
                            <StyledAtBatHeader
                                colSpan={6 + (pitcherRepertoire?.length || 0)}
                                ref={(elem) => {
                                    if (elem) {
                                        elem.style.setProperty('background-color', styles.theme.background.astronaut, 'important');
                                    }
                                }}
                            >
                                {headerText.map((x, idx) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <React.Fragment key={idx}>
                                        {idx > 0 && <span>|</span>}
                                        <span>{x}</span>
                                    </React.Fragment>
                                ))}
                            </StyledAtBatHeader>
                        </tr>
                        <tr>
                            <th />
                            <th>{translate('baseball.count')}</th>
                            <th>{translate('sections.post-game.pitch-execution.pitch-type')}</th>
                            <th>{translate('sections.post-game.pitch-execution.pitch-velo')}</th>
                            <th>{translate('sections.post-game.pitch-execution.resullt')}</th>
                            <th>{translate('sections.post-game.pitch-execution.flag')}</th>
                            {pitcherRepertoire?.map((r) => (
                                <th key={(r.pitchtype * 100) + r.fxsubtype} style={{ width: '104px' }}>{pitchTypeDisplayName({ pitchtype: r.pitchtype, fxsubtype: r.fxsubtype }, true, true, translate)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {mappedPitches.map((p, idx) => (
                            <AtBatTableRow key={`${p.eventseq}-${p.pitchseq}`} pitch={p} gameid={gameid} isFirstInGroup={idx === 0} canFlagPitch={canFlagPitch} isInBats={isInBats} />
                        ))}
                    </tbody>
                </React.Fragment>
            </Table>
        );
    });
};

PitchExecutionTable.propTypes = {
    playerid: PropTypes.number.isRequired,
    pitches: PropTypes.arrayOf(PropTypes.shape({
        inning: PropTypes.number.isRequired,
        batterid: PropTypes.number.isRequired,
        islhb: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
        outsbefore: PropTypes.number.isRequired,
        bcbefore: PropTypes.number.isRequired,
    })).isRequired,
    heatMaps: PropTypes.arrayOf(PropTypes.shape({
        BatterID: PropTypes.number.isRequired,
    })).isRequired,
    pitchReviews: PropTypes.arrayOf(PropTypes.shape({
        GameID: PropTypes.number.isRequired,
        EventSeq: PropTypes.number.isRequired,
        PitchSeq: PropTypes.number.isRequired,
    })).isRequired,
    ctwb: PropTypes.shape({}).isRequired,
    gameid: PropTypes.number.isRequired,
    flaggedPitchFilter: PropTypes.bool,
    isInBats: PropTypes.bool,
};
PitchExecutionTable.defaultProps = {
    flaggedPitchFilter: false,
};

export default PitchExecutionTable;
